import PrintSheet, { SheetTitle } from "@/modules/print/sheet";
import { SnapshotData } from "@buildpy/core/modules/quote/types";
import { SQM_PER_PYEONG } from "@buildpy/core/modules/quote/types";
import { ROUGH_TOTAL_LABEL, snapshotVatOf, SUPPLY_LABEL, VAT_LABEL } from "@buildpy/core/modules/quote/snapshot-vat";
import type { ReactNode } from "react";

import Money from "@/components/money";

interface Props {
  data: SnapshotData;
  /** 현재 조직 정보. 주면 회사 정보를 이걸로 덮는다. */
  org?: { name: string; tel: string; address: string };
}

const Row = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex items-baseline justify-between border-b border-[#e5e5e5] py-2">
    <span className="text-sm text-[#525252]">{label}</span>
    <span className="tabular-nums">{value}</span>
  </div>
);

/**
 * 개산서 **한 장**. 가견적은 「대략 얼마인가」 하나만 답한다.
 *
 * 상세견적처럼 갑지·간편견적서·상세견적서 3부로 나가지 않는다 — 공종도 항목도
 * 없는 견적에 목차를 세 번 붙이면, 고객은 빈 표 두 장을 받는다.
 *
 * 개산이라는 것을 **문서가 스스로 말하게** 한다. 고객이 이 숫자를 확정 금액으로
 * 읽으면 나중에 상세견적에서 오르내릴 때 분쟁이 된다.
 */
const QuoteRough = ({ data, org }: Props) => {
  const rough = data.rough;
  if (!rough) return null;

  const company = org ?? data.org;
  const vat = snapshotVatOf(data);
  const excl = vat.mode === "EXCL";

  return (
    <PrintSheet>
      <SheetTitle>가견적서 (개산)</SheetTitle>

      <div className="mb-4 flex flex-col gap-y-1 text-sm">
        <span className="text-[#525252]">
          {data.quote.quote_no} · v{data.quote.version}
        </span>
        <span className="text-lg font-semibold">{data.quote.title}</span>
        <span className="text-[#525252]">
          {data.customer.name} 고객
          {data.customer.site_address !== "" &&
            ` · ${data.customer.site_address}`}
        </span>
      </div>

      <div className="flex flex-col">
        <Row
          label="면적"
          value={`${rough.area_pyeong}평 (약 ${(
            rough.area_pyeong * SQM_PER_PYEONG
          ).toFixed(1)}㎡)`}
        />
        <Row label="평당 단가" value={<Money value={rough.unit_price} />} />
        {/* 별도 가견적은 면적 × 단가가 공급가 — 상세견적 갑지처럼 공급가 + 부가세로 나눠 적는다. */}
        {excl && <Row label={SUPPLY_LABEL} value={<Money value={vat.supply} />} />}
        {excl && <Row label={VAT_LABEL} value={<Money value={vat.vat} />} />}
        <div className="flex items-baseline justify-between py-3" data-testid="quote-rough-amount">
          <span className="font-medium">{ROUGH_TOTAL_LABEL}</span>
          <Money className="text-2xl font-semibold" value={rough.amount} />
        </div>
      </div>

      <div className="mt-4 rounded border border-[#e5e5e5] bg-[#fafafa] p-3 text-xs text-[#525252]">
        <p className="font-medium text-[#171717]">
          이 금액은 개산입니다.
        </p>
        <p className="mt-1">
          면적과 평당 단가로 계산한 대략의 금액이며, 현장 실측과 공종별 상세
          견적에서 달라집니다. 확정 금액은 상세 견적서로 안내드립니다.
        </p>
      </div>

      {data.quote.extra_notice !== "" && (
        <p className="mt-3 whitespace-pre-wrap text-xs text-[#525252]">
          {data.quote.extra_notice}
        </p>
      )}

      {data.quote.conditions.length > 0 && (
        <ul className="mt-3 flex list-disc flex-col gap-y-1 pl-4 text-xs text-[#525252]">
          {data.quote.conditions.map((line, i) => (
            <li key={`${i}-${line}`}>{line}</li>
          ))}
        </ul>
      )}

      <div className="mt-6 border-t border-[#e5e5e5] pt-3 text-xs text-[#525252]">
        <div className="font-medium text-[#171717]">{company.name}</div>
        {company.tel !== "" && <div>{company.tel}</div>}
        {company.address !== "" && <div>{company.address}</div>}
      </div>
    </PrintSheet>
  );
};

export default QuoteRough;
